import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../../components/ui/command";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";
import {
  ArrowLeft, Download, FileSpreadsheet, FileText, RefreshCw, File, FileJson,
  Users, ShieldCheck, ShieldAlert, Search, CheckCircle2, XCircle, AlertTriangle, ChevronsUpDown, Check
} from "lucide-react";
import { useAuth } from "../../App";
import { toast } from "sonner";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import { applyPlugin } from "jspdf-autotable";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from "recharts";
import { fmtBs } from "./reportesUtils";

applyPlugin(jsPDF);

const StatusBadge = ({ estatus }) => {
  if (estatus === "Solvente") return <Badge className="bg-green-100 text-green-700 border-green-200 text-xs"><CheckCircle2 className="w-3 h-3 mr-1" />Solvente</Badge>;
  if (estatus === "Moroso") return <Badge className="bg-red-100 text-red-700 border-red-200 text-xs"><XCircle className="w-3 h-3 mr-1" />Moroso</Badge>;
  return <Badge variant="outline" className="text-slate-400 text-xs">Sin registro</Badge>;
};

const MorosidadGeneral = () => {
  const navigate = useNavigate();
  const { API, token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [filtroLae, setFiltroLae] = useState("");
  const [filtroRif, setFiltroRif] = useState("");
  const [filtroGeneral, setFiltroGeneral] = useState("todos");
  const [contribuyentes, setContribuyentes] = useState([]);
  const [laePopoverOpen, setLaePopoverOpen] = useState(false);
  const [rifPopoverOpen, setRifPopoverOpen] = useState(false);

  useEffect(() => { fetchData(); fetchContribuyentes(); }, []);

  const fetchContribuyentes = async () => {
    try {
      const resp = await axios.get(`${API}/contribuyentes/con-lae`, { headers });
      setContribuyentes(resp.data);
    } catch { /* ignore */ }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      let url = `${API}/reportes/morosidad-general-lae`;
      const params = [];
      if (filtroLae) params.push(`nro_lae=${filtroLae}`);
      if (filtroRif) params.push(`rif=${filtroRif}`);
      if (params.length) url += "?" + params.join("&");
      const response = await axios.get(url, { headers });
      setData(response.data);
    } catch { toast.error("Error al cargar el reporte"); }
    setLoading(false);
  };

  const filteredData = () => {
    if (!data) return [];
    let filas = data.filas;
    if (filtroGeneral === "solvente") filas = filas.filter(f => f.moroso_count === 0);
    else if (filtroGeneral === "moroso") filas = filas.filter(f => f.moroso_count > 0);
    return filas;
  };

  const buscar = () => { fetchData(); };

  const limpiar = () => { setFiltroLae(""); setFiltroRif(""); setFiltroGeneral("todos"); setTimeout(fetchData, 100); };

  // Exports
  const exportToExcel = () => {
    const rows = filteredData().map(f => ({
      "RIF": f.rif, "Nombre": f.nombre, "N LAE": f.nro_lae, "Estado General": f.estatus_general,
      "Act. Económicas": f.actividades_economicas.estatus, "Últ. Período AE": f.actividades_economicas.ultimo_periodo,
      "Publicidad": f.publicidad.estatus, "Pendientes Pub.": f.publicidad.pendientes, "Monto Pub.": f.publicidad.monto,
      "Solvencia Inmob.": f.solvencia_inmobiliaria.estatus, "Conformidad Uso": f.conformidad_uso.estatus
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Morosidad General");
    XLSX.writeFile(wb, `morosidad_general_lae_${new Date().toISOString().split("T")[0]}.xlsx`);
  };

  const exportToPDF = () => {
    const doc = new jsPDF({ orientation: "landscape" });
    doc.setFontSize(14);
    doc.text("Morosidad General con LAE", 14, 15);
    doc.setFontSize(9);
    doc.text(`Generado: ${new Date().toLocaleDateString("es-VE")} | Total: ${filteredData().length} contribuyentes`, 14, 22);
    doc.autoTable({
      startY: 28,
      head: [["RIF", "Nombre", "LAE", "General", "Act. Econ.", "Publicidad", "Solv. Inmob.", "Conf. Uso"]],
      body: filteredData().map(f => [
        f.rif, f.nombre, f.nro_lae, f.estatus_general,
        f.actividades_economicas.estatus, f.publicidad.estatus,
        f.solvencia_inmobiliaria.estatus, f.conformidad_uso.estatus
      ]),
      styles: { fontSize: 7 },
      headStyles: { fillColor: [30, 41, 59] },
      didParseCell: (data) => {
        if (data.section === "body") {
          const v = data.cell.raw;
          if (v === "Solvente") data.cell.styles.textColor = [22, 163, 74];
          else if (v === "Moroso") data.cell.styles.textColor = [220, 38, 38];
        }
      }
    });
    doc.save(`morosidad_general_lae_${new Date().toISOString().split("T")[0]}.pdf`);
  };

  const exportToCSV = () => {
    const rows = filteredData();
    const h = "RIF,Nombre,LAE,General,Act. Econ.,Publicidad,Solv. Inmob.,Conf. Uso\n";
    const csv = h + rows.map(f =>
      `${f.rif},"${(f.nombre || "").replace(/"/g, '""')}",${f.nro_lae},${f.estatus_general},${f.actividades_economicas.estatus},${f.publicidad.estatus},${f.solvencia_inmobiliaria.estatus},${f.conformidad_uso.estatus}`
    ).join("\n");
    saveAs(new Blob([csv], { type: "text/csv;charset=utf-8" }), `morosidad_general_lae_${new Date().toISOString().split("T")[0]}.csv`);
  };

  const rows = filteredData();
  const resumen = data?.resumen || {};

  // Chart data
  const pieGeneral = resumen.total_contribuyentes ? [
    { name: "Solventes", value: resumen.total_solventes || 0 },
    { name: "Morosos", value: resumen.total_morosos || 0 }
  ] : [];

  const barModulos = [
    { modulo: "Act. Econ.", solvente: resumen.actividades_economicas?.solvente || 0, moroso: resumen.actividades_economicas?.moroso || 0 },
    { modulo: "Publicidad", solvente: resumen.publicidad?.solvente || 0, moroso: resumen.publicidad?.moroso || 0 },
    { modulo: "Solv. Inmob.", solvente: resumen.solvencia_inmobiliaria?.solvente || 0, moroso: resumen.solvencia_inmobiliaria?.moroso || 0 },
    { modulo: "Conf. Uso", solvente: resumen.conformidad_uso?.solvente || 0, moroso: resumen.conformidad_uso?.moroso || 0 }
  ];

  // Moroso count distribution
  const morosoDistrib = [0, 0, 0, 0, 0];
  (data?.filas || []).forEach(f => { morosoDistrib[f.moroso_count]++; });
  const barDistrib = [
    { label: "0 (Solvente)", cantidad: morosoDistrib[0] },
    { label: "1 concepto", cantidad: morosoDistrib[1] },
    { label: "2 conceptos", cantidad: morosoDistrib[2] },
    { label: "3 conceptos", cantidad: morosoDistrib[3] },
    { label: "4 conceptos", cantidad: morosoDistrib[4] }
  ].filter(d => d.cantidad > 0);

  return (
    <div className="space-y-6" data-testid="reporte-morosidad-general">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate("/reportes")} data-testid="btn-volver-reportes">
          <ArrowLeft className="w-5 h-5 mr-2" /> Volver
        </Button>
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Morosidad General con LAE</h2>
          <p className="text-sm text-slate-500">Estado integral de morosidad por contribuyente - {resumen.anio || new Date().getFullYear()}</p>
        </div>
      </div>

      {/* Filtros de búsqueda */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex items-end gap-3 flex-wrap">
            <div className="space-y-1">
              <label className="text-xs font-medium text-slate-500">Nro. LAE</label>
              <Popover open={laePopoverOpen} onOpenChange={setLaePopoverOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="h-9 w-52 justify-between font-normal" data-testid="filtro-lae">
                    {filtroLae ? <span className="truncate">{filtroLae}</span> : <span className="text-slate-400">Buscar LAE...</span>}
                    <ChevronsUpDown className="ml-2 h-3.5 w-3.5 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[320px] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Buscar por Nro. LAE o nombre..." />
                    <CommandList>
                      <CommandEmpty>No encontrado</CommandEmpty>
                      <CommandGroup>
                        {contribuyentes.map(c => (
                          <CommandItem key={c.id} value={`${c.numero_lae} ${c.nombre || c.denominacion_comercial || ""}`}
                            onSelect={() => { setFiltroLae(c.numero_lae); setLaePopoverOpen(false); }}>
                            <Check className={`mr-2 h-4 w-4 ${filtroLae === c.numero_lae ? "opacity-100" : "opacity-0"}`} />
                            <div className="flex flex-col">
                              <span className="font-medium text-sm">LAE: {c.numero_lae}</span>
                              <span className="text-xs text-slate-500 truncate">{c.nombre || c.denominacion_comercial}</span>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-slate-500">RIF</label>
              <Popover open={rifPopoverOpen} onOpenChange={setRifPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="h-9 w-56 justify-between font-normal" data-testid="filtro-rif">
                    {filtroRif ? <span className="truncate">{filtroRif}</span> : <span className="text-slate-400">Buscar RIF...</span>}
                    <ChevronsUpDown className="ml-2 h-3.5 w-3.5 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[380px] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Buscar por RIF o nombre..." />
                    <CommandList>
                      <CommandEmpty>No encontrado</CommandEmpty>
                      <CommandGroup>
                        {contribuyentes.map(c => {
                          const rifFull = `${c.tipo_rif || "J"}-${c.rif}`;
                          return (
                            <CommandItem key={c.id} value={`${rifFull} ${c.nombre || c.denominacion_comercial || ""}`}
                              onSelect={() => { setFiltroRif(c.rif); setRifPopoverOpen(false); }}>
                              <Check className={`mr-2 h-4 w-4 ${filtroRif === c.rif ? "opacity-100" : "opacity-0"}`} />
                              <div className="flex flex-col">
                                <span className="font-medium text-sm">{rifFull}</span>
                                <span className="text-xs text-slate-500 truncate">{c.nombre || c.denominacion_comercial}</span>
                              </div>
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-slate-500">Estado</label>
              <select className="h-9 border rounded-md px-2 text-sm bg-white" value={filtroGeneral} onChange={e => setFiltroGeneral(e.target.value)} data-testid="filtro-estado">
                <option value="todos">Todos</option>
                <option value="solvente">Solo Solventes</option>
                <option value="moroso">Solo Morosos</option>
              </select>
            </div>
            <Button className="h-9 gap-2 bg-slate-800" onClick={buscar} disabled={loading} data-testid="btn-buscar">
              <Search className="w-4 h-4" /> Buscar
            </Button>
            <Button variant="outline" className="h-9 gap-2" onClick={limpiar}>Limpiar</Button>
          </div>
        </CardContent>
      </Card>

      {/* Tarjetas resumen */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-slate-700">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-500 uppercase">Total Contribuyentes</p>
              <p className="text-2xl font-bold text-slate-800">{resumen.total_contribuyentes || 0}</p>
            </div>
            <Users className="w-8 h-8 text-slate-300" />
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-500 uppercase">Solventes (4/4)</p>
              <p className="text-2xl font-bold text-green-700">{resumen.total_solventes || 0}</p>
            </div>
            <ShieldCheck className="w-8 h-8 text-green-300" />
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-red-500">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-500 uppercase">Con Morosidad</p>
              <p className="text-2xl font-bold text-red-700">{resumen.total_morosos || 0}</p>
            </div>
            <ShieldAlert className="w-8 h-8 text-red-300" />
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-500 uppercase">% Morosidad</p>
              <p className="text-2xl font-bold text-amber-700">
                {resumen.total_contribuyentes ? ((resumen.total_morosos / resumen.total_contribuyentes) * 100).toFixed(1) : 0}%
              </p>
            </div>
            <AlertTriangle className="w-8 h-8 text-amber-300" />
          </CardContent>
        </Card>
      </div>

      {/* Gráficos */}
      {data && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Pie: General */}
          <Card>
            <CardContent className="p-4">
              <h3 className="text-sm font-semibold text-slate-700 mb-3">Estado General</h3>
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={pieGeneral} cx="50%" cy="50%" outerRadius={85} dataKey="value" nameKey="name"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                    <Cell fill="#22c55e" />
                    <Cell fill="#ef4444" />
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Barras: Por módulo */}
          <Card>
            <CardContent className="p-4">
              <h3 className="text-sm font-semibold text-slate-700 mb-3">Morosidad por Concepto</h3>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={barModulos}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="modulo" tick={{ fontSize: 10 }} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="solvente" name="Solvente" fill="#22c55e" stackId="a" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="moroso" name="Moroso" fill="#ef4444" stackId="a" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Barras: Distribución conceptos morosos */}
          <Card>
            <CardContent className="p-4">
              <h3 className="text-sm font-semibold text-slate-700 mb-3">Conceptos Morosos por Contribuyente</h3>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={barDistrib}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="cantidad" name="Contribuyentes" radius={[4, 4, 0, 0]}>
                    {barDistrib.map((entry, i) => (
                      <Cell key={i} fill={entry.label.startsWith("0") ? "#22c55e" : i === 1 ? "#fbbf24" : i === 2 ? "#f97316" : "#ef4444"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex items-center gap-2 flex-wrap">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="gap-2" data-testid="btn-exportar"><Download className="w-4 h-4" /> Exportar</Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuItem onClick={exportToExcel} className="gap-2 cursor-pointer"><FileSpreadsheet className="w-4 h-4" /> Excel</DropdownMenuItem>
            <DropdownMenuItem onClick={exportToPDF} className="gap-2 cursor-pointer"><FileText className="w-4 h-4" /> PDF</DropdownMenuItem>
            <DropdownMenuItem onClick={exportToCSV} className="gap-2 cursor-pointer"><File className="w-4 h-4" /> CSV</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <Button variant="outline" onClick={fetchData} disabled={loading} className="gap-2">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Actualizar
        </Button>
        <Badge variant="outline" className="ml-auto text-sm">{rows.length} de {data?.filas?.length || 0} contribuyentes</Badge>
      </div>

      {/* Tabla */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="tabla-morosidad-general">
              <thead>
                <tr className="border-b bg-slate-800 text-white">
                  <th className="px-3 py-3 text-left text-xs font-semibold uppercase">RIF</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold uppercase">Nombre</th>
                  <th className="px-3 py-3 text-center text-xs font-semibold uppercase">LAE</th>
                  <th className="px-3 py-3 text-center text-xs font-semibold uppercase">Estado General</th>
                  <th className="px-3 py-3 text-center text-xs font-semibold uppercase">Act. Económicas</th>
                  <th className="px-3 py-3 text-center text-xs font-semibold uppercase">Publicidad</th>
                  <th className="px-3 py-3 text-center text-xs font-semibold uppercase">Solv. Inmobiliaria</th>
                  <th className="px-3 py-3 text-center text-xs font-semibold uppercase">Conf. de Uso</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={8} className="py-12 text-center text-slate-400">Cargando...</td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan={8} className="py-12 text-center text-slate-400">No hay registros</td></tr>
                ) : rows.map((f, i) => (
                  <tr key={i} className={`border-b transition-colors ${f.moroso_count === 0 ? "hover:bg-green-50/30" : f.moroso_count >= 3 ? "hover:bg-red-50/30 bg-red-50/10" : "hover:bg-amber-50/30"}`}>
                    <td className="px-3 py-2.5 text-sm font-mono text-slate-700">{f.rif}</td>
                    <td className="px-3 py-2.5 text-sm text-slate-700 max-w-[180px] truncate">{f.nombre}</td>
                    <td className="px-3 py-2.5 text-sm font-mono text-slate-600 text-center">{f.nro_lae}</td>
                    <td className="px-3 py-2.5 text-center">
                      {f.moroso_count === 0 ? (
                        <Badge className="bg-green-600 text-white text-xs">Solvente</Badge>
                      ) : (
                        <Badge className="bg-red-600 text-white text-xs">{f.estatus_general}</Badge>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-center"><StatusBadge estatus={f.actividades_economicas.estatus} /></td>
                    <td className="px-3 py-2.5 text-center"><StatusBadge estatus={f.publicidad.estatus} /></td>
                    <td className="px-3 py-2.5 text-center"><StatusBadge estatus={f.solvencia_inmobiliaria.estatus} /></td>
                    <td className="px-3 py-2.5 text-center"><StatusBadge estatus={f.conformidad_uso.estatus} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MorosidadGeneral;
